/* assets/autolinks.js
   Interlinking based on menu (autolinks-data.liquid) + vendor forcing.
   Fixes:
   - Includes price/product info (so "per rol" can be linked)
   - Vendor always linked (unlimited) with variants (&/and/und)
   - Tabs/accordions incl hidden panels (process on open)
   - Clean rule: menu headers (#) are not targets
   - Excludes breadcrumbs completely (no linking inside breadcrumb nav)
   - Excludes ALL navigation (header/footer/menus/drawers) from interlinking
   - Excludes collection filters sidebar (collection-sidebar.liquid / facets) from interlinking

   10-09-2026: merk-hub-skip via ctx.brand_hub (server-side, taalonafhankelijk) naast de
   handle-regex, die alleen NL/EN/DE-handles herkende (FR/IT/ES-hubs kregen wel autolinks).

   Data from autolinks-data.liquid:
     item.t   = menu title (fallback)
     item.to  = linked object.title in current language (preferred)
     item.u   = url
     item.top = top-level group label (e.g. Behang/Tapete/Wallpaper)
*/

(function () {
  "use strict";

  const DATA_ID = "äuto-links-data";
  const CTX_ID  = "nw-autolinks-context";
  const BRAND_PAGE_RE = /-(behang|tapete|wallpaper)$/i;
  const SUB_COLLECTION_RE = /^(behangcollectie-|tapetenkollektion-|wallpapercollection-|wallpaper-collection-|murals?-|fotobehang-|kleur-|farbe-|color-|colour-|stof-|stoffen-|stoffe-|fabric-|kussen-|kussens-|kissen-|cushion-|verf-|paint-|lijm-|glue-|behang-themas?-|behang-kleuren-|behang-materiaal-)/i;

  // Breadcrumb selectors to exclude (add more if your theme uses different markup)
  const BREADCRUMB_SELECTORS = [
    "nav.breadcrumb",
    "nav.breadcrumbs",
    ".breadcrumb",
    ".breadcrumbs",
    ".breadcrumbs__list",
    ".breadcrumb__list",
    ".breadcrumb-list",
    ".site-breadcrumbs",
    "[aria-label='breadcrumbs']",
    "[aria-label='breadcrumb']"
  ];

  // Navigation selectors to exclude (header/footer menus, drawers, mobile nav, etc.)
  const NAV_EXCLUDE_SELECTORS = [
    "header",
    "footer",
    ".header",
    ".site-header",
    ".main-header",
    ".footer",
    ".site-footer",
    ".main-footer",
    ".menu",
    ".menu-drawer",
    ".drawer",
    ".drawer__inner",
    ".navigation",
    ".site-nav",
    ".main-nav",
    ".mobile-nav",
    ".mobile-nav__drawer",
    ".header__menu",
    ".header__inline-menu",
    ".footer__content",
    ".footer__menu",
    "[role='navigation']",
    "[data-menu-drawer]",
    "[data-drawer]",
    "[data-nav]",
    "[data-header]",
    "[data-footer]"
  ];

  // Exclude only the collection sidebar / filters snippet (collection-sidebar.liquid)
  const COLLECTION_SIDEBAR_SELECTORS = [
    // Snippet wrapper(s) (collection-sidebar.liquid)
    ".collection-sidebar",
    "#collection-sidebar",
    "#CollectionSidebar",
    "[data-collection-sidebar]",
    ".collection-sidebar__filters",
    ".collection-sidebar__inner",
    ".collection-sidebar__content",

    // Shopify/Dawn "facets" filter markup (and many derivatives)
    ".facets",
    ".facets-wrapper",
    ".facets__wrapper",
    ".facets__form",
    ".facets__main",
    "#FacetFiltersForm",
    "#CollectionFiltersForm",
    "[data-facets]",
    "[data-facet-filters]",
    "[data-facet-form]",
    "[data-facet]",
    "[data-collection-filters]",
    "[data-collection-filters-form]",

    // Common naming in other themes
    ".collection-filters",
    ".collection__filters",
    ".collection__filters-wrapper",
    ".collection__filters-form",
    ".collection__sidebar",
    ".collection-sidebar-filters",
    ".sidebar--collection",
    ".sidebar__filters",

    // Popular filter apps (best-effort)
    ".boost-pfs-filter-tree",
    ".boost-pfs-filter-wrapper",
    ".gf-filter-contents",
    ".gf-filter-container",
    ".snize-filters"
  ];


  function isInBreadcrumb(el) {
    if (!el || el.nodeType !== 1) return false;
    for (const sel of BREADCRUMB_SELECTORS) {
      if (el.closest && el.closest(sel)) return true;
    }
    return false;
  }

  function isInNavigation(el) {
    if (!el || el.nodeType !== 1) return false;
    for (const sel of NAV_EXCLUDE_SELECTORS) {
      if (el.closest && el.closest(sel)) return true;
    }
    return false;
  }

  function isInCollectionSidebar(el) {
    if (!el || el.nodeType !== 1) return false;

    // Always respect explicit skip marker (we also auto-add it for collection filters)
    if (el.closest && el.closest("[data-nw-autolinks-skip]")) return true;

    // Only treat "filter selectors" as sidebar in collection/search context,
    // to avoid accidental exclusions on other templates.
    if (!IS_COLLECTION_CONTEXT) return false;

    for (const sel of COLLECTION_SIDEBAR_SELECTORS) {
      if (el.closest && el.closest(sel)) return true;
    }
    return false;
  }

  // Exclude hover-only UI elements (tooltips, popovers, hover overlays, quick-view, etc.)
  const HOVER_EXCLUDE_SELECTORS = [
    "[role='tooltip']",
    "[data-tooltip]",
    "[data-tooltips]",
    "[data-tippy-root]",
    ".tooltip",
    ".tool-tip",
    ".popover",
    ".tippy-box",
    ".tippy-content",
    ".hover-overlay",
    ".product-card__hover",
    ".card__hover",
    ".quickview",
    ".quick-view",
    ".quick-add",
    ".product-card__quickview",
    ".product-card__overlay",
    ".product-card__actions",
    ".product-card__buttons",
    ".product-card__info-hover",
    ".swatch",
    ".swatches",
    ".swatch__tooltip",
    ".swatch-tooltip",
    ".swatchToolTip",
    ".variant-swatch",
    ".variant__swatch",
    ".product-swatch",
    ".product__swatch",
    ".color-swatch",
    ".colour-swatch",
    ".colorswatch",
    ".colourSwatch",
    ".swatch-element",
    ".swatchElement",
    ".swatch-option",
    ".swatchOption",
    "[data-swatch]",
    "[data-swatch-tooltip]",
    "[data-swatch-option]",
    "[data-variant-swatch]",
    ".loox-swatch",
    ".loox-swatch-tooltip",
    "[class*='tooltip']",
    "[class*='Tooltip']",
    "[class*='tippy']",
    "[class*='popover']",
    "[class*='swatch']",
    "[class*='Swatch']",
    "[data-label]",
    "[data-tooltip-text]",
    ".visually-hidden",
    ".visuallyHidden",
    ".sr-only",
    ".sr_only",
    ".srOnly",
    ".screen-reader-only",
    ".screenReaderOnly",
    ".u-visually-hidden",
    ".a11y-hidden",
    "[class*='visually-hidden']",
    "[class*='VisuallyHidden']",
    "[class*='sr-only']",
    "[class*='srOnly']",
    "[class*='screen-reader']",
    "[class*='screenReader']",
    "[data-option-name]",
    "[data-option-value]",
    "[data-variant-picker]",
    "[data-variant-option]",
    ".variant-picker",
    ".variant-picker__option",
    ".variant-option",
    ".product-form__swatches",
    ".product-form__swatch",
    ".product-form__input--swatch",
  ];


  const HOVER_EXCLUDE_SELECTOR = HOVER_EXCLUDE_SELECTORS.join(",");


  function isInHoverUI(el) {
    if (!el || el.nodeType !== 1) return false;

    // Treat as hover UI when inside known tooltip/popover/swatch-tooltip containers.
    // We exclude these ALWAYS (even when visible) to prevent interlinking inside hover labels.
    const hoverContainerSelector = HOVER_EXCLUDE_SELECTOR;
    const container = el.closest && el.closest(hoverContainerSelector);
    if (!container) return false;

    // Strong signals: tooltip/popover/swatch tooltip
    if (
      (container.getAttribute && (container.getAttribute("role") || "").toLowerCase() === "tooltip") ||
      (container.matches && container.matches("[role='tooltip'],[data-tooltip],[data-tippy-root],.tooltip,.tool-tip,.popover,.tippy-box,.tippy-content,.swatch__tooltip,.swatch-tooltip,.swatchToolTip,[class*='tooltip'],[class*='tippy'],[class*='popover']"))
    ) {
      return true;
    }

    // Swatch-related wrappers: exclude (these are typically hover labels)
    if (container.matches && container.matches("[class*='swatch'],[class*='Swatch'],[data-swatch],[data-swatch-tooltip],[data-variant-swatch]")) {
      return true;
    }

    // If container is aria-hidden or visually hidden, exclude
    if (container.getAttribute && container.getAttribute("aria-hidden") === "true") return true;
    const style = window.getComputedStyle(container);
    if (style && (style.display === "none" || style.visibility === "hidden")) return true;

    return false;
  }

  function isInHeadingBlock(el) {
    if (!el || el.nodeType !== 1) return false;
    for (const sel of HEADING_BLOCK_SELECTORS) {
      if (el.closest && el.closest(sel)) return true;
    }
    return false;
  }

  const ROOT_SELECTORS = [
    // Product description / RTE
    ".tb_pr_desc",
    "#ptabDesc",
    "#ptabDesc .rte",
    ".product__description",
    ".product__description .rte",
    ".product-single__description",
    ".rte",

    // Product info / price
    ".product__info-container",
    ".product__info-wrapper",
    ".product__info",
    ".product__content",
    ".product-form",
    ".product-form__buttons",
    ".product__price",
    ".price",
    ".price__container",
    ".price-item",
    "[data-price]",

    // Product cards / grids
    ".product-card",
    ".product-card__info",
    ".card__information",
    ".grid-product__meta",
    ".grid-product__title",
    ".grid-product__vendor",
    ".product-item__info",
    ".product-item__vendor",
    "[data-product-card]",

    // Tabs
    ".tab-content",
    ".tabs__content",
    ".tabs-content",
    "[role='tabpanel']",
    "[data-tab-content]",
    "[data-tabs-content]",

    // Product specs / details
    ".specValue",
    ".specItem",
    ".specGrid",

    // Accordions / collapsibles
    ".accordion",
    ".accordion__content",
    ".accordion__panel",
    ".accordion-content",
    ".accordion-panel",
    ".collapsible-content",
    ".collapsible-content__inner",
    ".collapsible-content__content",
    ".collapsible",
    ".collapsible__content",
    ".collapse",
    ".collapse__content",
    "[data-accordion-content]",
    "[data-collapsible-content]",

    // Details/Summary accordions
    "details",
    "details .accordion__content",
    "details .collapsible-content__inner",

    // Generic fallbacks (visible only)
    ".shopify-section",
    ".section",
    "[data-section-type]",
    "[data-section-id]",

    // Collection / page / blog
    ".collection__description",
    ".collection__description .rte",
    ".collection-description",
    ".collection-description .rte",
    "article",
    "article .rte"
  ];

  const SKIP_TAGS = new Set([
    "A","SCRIPT","STYLE","NOSCRIPT","TEXTAREA","INPUT","SELECT","OPTION","BUTTON",
    "CODE","PRE","KBD","SAMP","VAR","SVG","MATH","IFRAME"
  ]);
  const HEADING_TAGS = new Set(["H1","H2","H3","H4","H5","H6"]);

  

  // Extra heading wrappers used by many themes (div/span headings, card headings, etc.)
  const HEADING_BLOCK_SELECTORS = [
    "[role='heading']",
    '.h1',
    '.h2',
    '.h3',
    '.h4',
    '.h5',
    '.h6',
    '.heading',
    '.section-heading',
    '.section-header',
    '.section-title',
    '.product__title',
    '.product-title',
    '.collection__title',
    '.collection-title',
    '.page-title',
    '.blog__title',
    '.article__title',
    '.card__heading',
    '.card__heading-link',
  ];
const MAX_LINKS_PER_ROOT = 260;
  const MAX_LINKS_PER_KEYWORD = 3;
  const MIN_PREFILTER_LEN = 3;
  const MIN_ROOT_TEXT_LEN = 12;

  const PROCESSED_ATTR = "data-nw-autolinks-processed";
  const RUN_DEBOUNCE_MS = 180;

  const TAG_RULES = [
    { tag: "mural",        cat: "murals"   },
    { tag: "kussen",       cat: "cushions" },
    { tag: "verf",         cat: "paint"    },
    { tag: "stof",         cat: "fabric"   },
    { tag: "lijm",         cat: "glue"     },
    { tag: "geenmaatwerk", cat: "wallpaper"}
  ];

  const TOP_MATCHERS = {
    wallpaper: ["behang", "tapete", "wallpaper"],
    murals:    ["mural", "murals", "wandbild", "wandbilder", "wall mural", "fotobehang"],
    cushions:  ["kussen", "kussens", "kissen", "cushion", "cushions"],
    paint:     ["verf", "farbe", "paint"],
    fabric:    ["stof", "stoffen", "stoffe", "fabric", "fabrics"],
    glue:      ["lijm", "behanglijm", "glue", "paste", "kleber", "kleister", "tapetenkleber", "tapetenkleister"]
  };

  const STRIP_WORDS = [
    "behang", "tapete", "wallpaper",
    "stof", "stoffen", "stoffe", "fabric", "fabrics",
    "kussen", "kussens", "kissen", "cushion", "cushions",
    "verf", "farbe", "paint",
    "collectie", "collecties", "behangcollectie", "tapetenkollektion", "kollektion", "collection", "collections"
  ];

  const COMPOUND_TOKENS = [
    "behang", "tapete", "wallpaper",
    "stoffen", "stof", "stoffe", "fabric",
    "kussens", "kussen", "kissen", "cushions", "cushion",
    "verf", "farbe", "paint",
    "murals", "mural"
  ];

  function safeParseJson(text) { try { return JSON.parse(text); } catch { return null; } }
  function decodeHtmlEntities(str) {
    const t = document.createElement("textarea");
    t.innerHTML = str;
    return t.value;
  }
  function normalizeKey(s) {
    return String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
  }
  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  function isUsableLinkUrl(url) {
    if (!url) return false;
    const u = String(url).trim().toLowerCase();
    if (!u) return false;
    if (u === "#" || u.startsWith("#")) return false;
    if (u.startsWith("javascript:")) return false;
    return true;
  }

  function getLocalePrefixFromPath(pathname) {
    const seg = (pathname || "/").split("/").filter(Boolean)[0] || "";
    if (!seg) return "";
    const looksLikeLocale = /^[a-z]{2}(?:-[a-z]{2})?$/i.test(seg);
    const isShopifyRoute = ["collections","products","pages","blogs","cart","search","account","policies","challenge"].includes(seg.toLowerCase());
    return (looksLikeLocale && !isShopifyRoute) ? ("/" + seg.toLowerCase()) : "";
  }
  const LOCALE_PREFIX = getLocalePrefixFromPath(window.location.pathname);

  const IS_COLLECTION_CONTEXT = (() => {
    const p = (window.location.pathname || "").toLowerCase();
    if (/(^|\/)collections(\/|$)/.test(p)) return true;
    if (/(^|\/)search(\/|$)/.test(p)) return true;

    const cls = (document.body && document.body.className) ? document.body.className.toLowerCase() : "";
    if (cls.includes("template-collection") || cls.includes("template-search")) return true;

    return false;
  })();


  function withLocalePrefix(url) {
    if (!url) return url;
    if (/^https?:\/\//i.test(url)) return url;
    if (/^\/\//.test(url)) return url;
    let u = url.startsWith("/") ? url : ("/" + url);
    if (LOCALE_PREFIX && (u === LOCALE_PREFIX || u.startsWith(LOCALE_PREFIX + "/"))) return u;
    if (LOCALE_PREFIX) u = LOCALE_PREFIX + u;
    return u.replace(/\/{2,}/g, "/");
  }

  function isElementVisible(el) {
    if (!el || el.nodeType !== 1) return false;
    const style = window.getComputedStyle(el);
    if (!style) return true;
    if (style.display === "none" || style.visibility === "hidden") return false;
    if (el.getClientRects && el.getClientRects().length === 0) return false;
    return true;
  }

  // Mark collection filter areas so the TreeWalker never interlinks inside them,
  // even when the theme's CSS selectors differ from our defaults.
  //
  // You can also add `data-nw-autolinks-skip` on the wrapper in collection-sidebar.liquid for a guaranteed skip.
  let _filtersMarkedOnce = false;
  function markCollectionFiltersAsSkip(scope) {
    if (!IS_COLLECTION_CONTEXT) return;

    // Avoid repeatedly scanning the entire document
    if (!scope || scope === document) {
      if (_filtersMarkedOnce) return;
      _filtersMarkedOnce = true;
    }

    const root = (scope && scope.querySelectorAll) ? scope : document;

    // 1) Known sidebar/filter wrappers
    try {
      const sel = COLLECTION_SIDEBAR_SELECTORS.join(",");
      root.querySelectorAll(sel).forEach((el) => {
        if (!el || el.nodeType !== 1) return;
        if (el.hasAttribute && el.hasAttribute("data-nw-autolinks-skip")) return;
        el.setAttribute("data-nw-autolinks-skip", "collection-filters");
      });
    } catch { /* ignore selector errors */ }

    // 2) Heuristic: forms that contain Shopify filter inputs
    const filterInputSelector = "input[name^='filter.'],select[name^='filter.'],input[name^='filter-'],select[name^='filter-'],[data-facet],[data-facets],[data-facet-filters]";
    root.querySelectorAll("form").forEach((form) => {
      if (!form || form.nodeType !== 1 || !form.querySelector) return;
      if (!form.querySelector(filterInputSelector)) return;

      const container =
        form.closest(".collection-sidebar,#collection-sidebar,#CollectionSidebar,[data-collection-sidebar],.facets-wrapper,.facets__wrapper,.facets,.collection-filters,.collection__filters,.collection__sidebar,.sidebar,aside") ||
        form;

      if (container && container.setAttribute && !container.hasAttribute("data-nw-autolinks-skip")) {
        container.setAttribute("data-nw-autolinks-skip", "collection-filters");
      }
    });

    // 3) Heuristic: inputs outside forms (some themes/apps)
    root.querySelectorAll(filterInputSelector).forEach((inp) => {
      if (!inp || inp.nodeType !== 1 || !inp.closest) return;

      const container =
        inp.closest(".collection-sidebar,#collection-sidebar,#CollectionSidebar,[data-collection-sidebar],.facets-wrapper,.facets__wrapper,.facets,.collection-filters,.collection__filters,.collection__sidebar,.sidebar,aside") ||
        null;

      if (container && container.setAttribute && !container.hasAttribute("data-nw-autolinks-skip")) {
        container.setAttribute("data-nw-autolinks-skip", "collection-filters");
      }
    });
  }


  function isSkippableNode(node) {
    let el = node.parentElement;
    while (el) {
      if (SKIP_TAGS.has(el.tagName)) return true;
      if (HEADING_TAGS.has(el.tagName)) return true;
      if (isInHeadingBlock(el)) return true;
      if (el.hasAttribute && el.hasAttribute("data-nw-autolinks-skip")) return true;

      // Exclusions
      if (isInBreadcrumb(el)) return true;
      if (isInNavigation(el)) return true;


      // Exclude hover-only UI elements (swatch tooltips, popovers, etc.)
      if (el.matches && el.matches(HOVER_EXCLUDE_SELECTOR)) return true;

      // Exclude collection sidebar / filters
      if (isInCollectionSidebar(el)) return true;
      el = el.parentElement;
    }
    return false;
  }

  function alreadyLinkedTextNode(node) {
    let p = node.parentElement;
    while (p) {
      if (p.tagName === "A") return true;
      p = p.parentElement;
    }
    return false;
  }

  function isWordChar(ch) {
    return /[0-9A-Za-zÀ-ÖØ-öø-ÿ]/.test(ch);
  }

  function readContext() {
    const el = document.getElementById(CTX_ID);
    if (!el) return {};
    const raw = (el.textContent || "").trim().replace(/^\uFEFF/, "");
    let ctx = safeParseJson(raw);
    if (!ctx) ctx = safeParseJson(decodeHtmlEntities(raw));
    return (ctx && typeof ctx === "object") ? ctx : {};
  }

  function readMenuItems() {
    const el = document.getElementById(DATA_ID);
    if (!el) return null;
    const raw = (el.textContent || "").trim().replace(/^\uFEFF/, "");
    let data = safeParseJson(raw);
    if (!data) data = safeParseJson(decodeHtmlEntities(raw));
    if (!data || typeof data !== "object" || !Array.isArray(data.items)) return null;
    return data.items;
  }

  function splitTags(joined) {
    if (!joined) return [];
    return String(joined).split("||").map(t => normalizeKey(t)).filter(Boolean);
  }

  function chooseCategory(ctx) {
    const pageType = normalizeKey(ctx.page_type || "");
    const tags = splitTags(ctx.product_tags_joined);

    if (pageType === "product") {
      for (const r of TAG_RULES) {
        if (tags.includes(normalizeKey(r.tag))) return r.cat;
      }
      return "wallpaper";
    }

    const ch = normalizeKey(ctx.collection_handle || "");
    const path = (window.location.pathname || "").toLowerCase();
    const looks = (s) => ch.includes(s) || path.includes(s);

    if (looks("mural") || looks("murals")) return "murals";
    if (looks("kussen") || looks("kussens") || looks("kissen") || looks("cushion") || looks("cushions")) return "cushions";
    if (looks("verf") || looks("farbe") || looks("paint")) return "paint";
    if (looks("stof") || looks("stoffen") || looks("stoffe") || looks("fabric") || looks("fabrics")) return "fabric";
    if (looks("lijm") || looks("glue") || looks("paste") || looks("kleber") || looks("kleister")) return "glue";
    return "wallpaper";
  }

  function topMatchesCategory(topTitle, category) {
    const top = normalizeKey(topTitle);
    // Glue/paste items are universal - relevant for all product categories
    if (top.includes("lijm")) return true;
    const list = TOP_MATCHERS[category] || [];
    for (const m of list) {
      if (top.includes(m)) return true;
    }
    return false;
  }

  function stripDescriptors(title) {
    let s = " " + normalizeKey(title) + " ";
    for (const w of STRIP_WORDS) {
      const rx = new RegExp("\\s+" + escapeRegExp(w) + "\\s+", "g");
      s = s.replace(rx, " ");
    }
    s = s.replace(/\s+/g, " ").trim();
    return s;
  }

  function addCompoundVariants(set, rawTitle) {
    const raw = String(rawTitle || "").trim();
    if (!raw) return;

    const low = normalizeKey(raw);

    for (const token of COMPOUND_TOKENS) {
      if (low.length <= token.length + 1) continue;
      if (low.endsWith(token) && !low.endsWith(" " + token)) {
        const base = low.slice(0, low.length - token.length).trim();
        if (base && base.length >= 2) {
          const spaced = (base + " " + token).trim();
          if (spaced.length >= 2) set.add(spaced);
        }
      }
    }
  }

  function addUnitVariants(set, rawTitle) {
    const low = normalizeKey(rawTitle);
    const m = low.match(/^(per|pro)\s+(.+)$/i);
    if (m && m[2]) {
      const rest = m[2].trim();
      if (rest.length >= 2) set.add(rest);
    }
  }

  function addAmpersandVariants(set, rawTitle) {
    const s = String(rawTitle || "").trim();
    if (!s) return;
    const low = normalizeKey(s);

    if (low.includes("&")) {
      set.add(s.replace(/&/g, "and"));
      set.add(s.replace(/&/g, "und"));
    }
    if (low.includes(" and ")) set.add(s.replace(/ and /gi, " & "));
    if (low.includes(" und ")) set.add(s.replace(/ und /gi, " & "));
  }

  function addPunctuationVariants(set, rawTitle) {
    const s = String(rawTitle || "").trim();
    if (!s) return;
    const noTrail = s.replace(/[.,:;!?)]+$/g, "").trim();
    if (noTrail && noTrail !== s) set.add(noTrail);

    const noDots = s.replace(/\./g, "").trim();
    if (noDots && noDots !== s) set.add(noDots);
  }

  function addTitleVariants(set, title) {
    const raw = String(title || "").trim();
    if (!raw || raw.length < 2) return;

    set.add(raw);
    addPunctuationVariants(set, raw);
    addAmpersandVariants(set, raw);

    const stripped = stripDescriptors(raw);
    if (stripped && stripped.length >= 2 && normalizeKey(stripped) !== normalizeKey(raw)) {
      set.add(stripped);
      addPunctuationVariants(set, stripped);
      addAmpersandVariants(set, stripped);
    }

    addUnitVariants(set, raw);
    addCompoundVariants(set, raw);
    if (stripped) {
      addUnitVariants(set, stripped);
      addCompoundVariants(set, stripped);
    }
  }

  function buildKeywordVariants(menuTitle, objectTitle) {
    const variants = new Set();
    if (objectTitle) addTitleVariants(variants, objectTitle);
    if (menuTitle) addTitleVariants(variants, menuTitle);
    return Array.from(variants);
  }

  function collectCandidateRoots(container) {
    const root = container || document;
    const found = [];
    for (const sel of ROOT_SELECTORS) {
      root.querySelectorAll(sel).forEach(el => {
        if (!el) return;
        if (el.hasAttribute(PROCESSED_ATTR)) return;

        // Exclude breadcrumbs / navigation at root-level
        if (el.closest && el.closest("[data-glue-product-upsell]")) return; // upsell-blok heeft eigen productlink
        if (isInBreadcrumb(el)) return;
        if (isInNavigation(el)) return;
        if (isInCollectionSidebar(el)) return;
        if (isInHoverUI(el)) return;

        if (isInCollectionSidebar(el)) return;
        const txt = (el.textContent || "").trim();
        if (txt.length < MIN_ROOT_TEXT_LEN) return;

        // Beschrijvings-roots zijn vrijgesteld van de zichtbaarheids-check (17-08):
        // de tab-inhoud is verborgen tijdens de eerste idle-pass en er vuurt geen
        // open-event voor de standaard-open tab -> de hoofd-beschrijving kreeg
        // daardoor nooit links, terwijl accordion-panelen de caps opsnoepten.
        const isDescRoot = !!(el.closest && el.closest(".tb_pr_desc, #ptabDesc, .product__description, .product-single__description"));
        if (!isElementVisible(el) && !isDescRoot) return;

        found.push(el);
      });
    }
    const uniq = [];
    const seen = new Set();
    for (const el of found) {
      if (seen.has(el)) continue;
      seen.add(el);
      uniq.push(el);
    }
    return uniq;
  }

  function buildPageNormFromAllText() {
    const main = document.querySelector("main") || document.body;
    return normalizeKey((main && main.textContent) ? main.textContent : "");
  }

  function findBestUrlForVendor(items, category, vendorRaw) {
    const v = normalizeKey(vendorRaw);
    if (!v) return "";

    for (const it of items) {
      if (!it) continue;
      const url = it.u ? String(it.u).trim() : "";
      if (!isUsableLinkUrl(url)) continue;
      if (!topMatchesCategory(it.top || "", category)) continue;

      const t = normalizeKey(it.t || "");
      const to = normalizeKey(it.to || "");

      if (t === v || to === v) return withLocalePrefix(url);
      if (t.includes(v) || to.includes(v)) return withLocalePrefix(url);
    }

    for (const it of items) {
      if (!it) continue;
      const url = it.u ? String(it.u).trim() : "";
      if (!isUsableLinkUrl(url)) continue;

      const t = normalizeKey(it.t || "");
      const to = normalizeKey(it.to || "");

      if (t === v || to === v) return withLocalePrefix(url);
      if (t.includes(v) || to.includes(v)) return withLocalePrefix(url);
    }

    return "";
  }

  function buildTargets(items, category, pageNorm, ctx) {
    const map = new Map();
    const vendorRaw = (ctx && ctx.vendor) ? String(ctx.vendor).trim() : "";

    for (const it of (items || [])) {
      const menuTitle = it && it.t ? String(it.t).trim() : "";
      const objTitle  = it && it.to ? String(it.to).trim() : "";
      const url       = it && it.u ? String(it.u).trim() : "";
      const top       = it && it.top ? String(it.top).trim() : "";

      if (!isUsableLinkUrl(url)) continue;
      if (!topMatchesCategory(top, category)) continue;
      // Finetune 17-08: merk-items (b:1) van een ANDER merk niet linken op
      // productpagina's — "Arte" in de lijm-upsell linkte naar de Arte-hub
      // op een Casamance-pagina (semantisch scheef + cross-brand-ruis).
      if (it.b && normalizeKey((ctx && ctx.page_type) || "") === "product") {
        const _v = normalizeKey(vendorRaw);
        if (_v && !normalizeKey(menuTitle).includes(_v) && !normalizeKey(objTitle).includes(_v)) continue;
      }

      const resolvedUrl = withLocalePrefix(url);

      const variants = buildKeywordVariants(menuTitle, objTitle);
      for (const kw of variants) {
        const n = normalizeKey(kw);
        if (n.length < 2) continue;
        if (n.length >= MIN_PREFILTER_LEN && !pageNorm.includes(n)) continue;
        if (!map.has(n)) map.set(n, { keyword: kw, url: resolvedUrl, isVendor: false });
      }
    }

    // Force vendor unlimited
    if (vendorRaw) {
      const vendorUrl = (ctx && typeof ctx.vendor_url === "string") ? ctx.vendor_url : findBestUrlForVendor(items || [], category, vendorRaw);
      if (vendorUrl) {
        const vSet = new Set();
        addTitleVariants(vSet, vendorRaw);
        vSet.add(vendorRaw.replace(/&/g, "and"));
        vSet.add(vendorRaw.replace(/&/g, "und"));

        for (const vkw of vSet) {
          const vn = normalizeKey(vkw);
          if (!vn) continue;
          map.set(vn, { keyword: vkw, url: vendorUrl, isVendor: true });
        }
      }
    }

    const keywords = Array.from(map.keys())
      .map(n => map.get(n).keyword)
      .sort((a, b) => b.length - a.length);

    return { map, keywords };
  }

  function buildRegexChunks(keywords) {
    const chunks = [];
    const chunkSize = 160;

    for (let i = 0; i < keywords.length; i += chunkSize) {
      const part = keywords.slice(i, i + chunkSize)
        .map(kw => escapeRegExp(kw).replace(/\s+/g, "\\s+"))
        .filter(Boolean);
      if (!part.length) continue;
      chunks.push(new RegExp("(" + part.join("|") + ")", "gi"));
    }
    return chunks;
  }

  function isSameUrl(a, b) {
    try {
      const ua = new URL(a, window.location.origin).pathname.replace(/\/+$/, "");
      const ub = new URL(b, window.location.origin).pathname.replace(/\/+$/, "");
      return ua === ub;
    } catch {
      return false;
    }
  }

  function linkifyRoot(root, regexChunks, map, usedCounts) {
    // Beschrijving = eigen vendor-budget (17-08): specs-tab en accordion-panelen
    // verbruikten de paginabrede cap steeds voordat de beschrijvings-tab openging.
    const rootIsDesc = !!(root.closest && root.closest(".tb_pr_desc, #ptabDesc, .product__description, .product-single__description"));
    if (!root || root.nodeType !== 1) return;
    if (root.hasAttribute(PROCESSED_ATTR)) return;
    if (!isElementVisible(root)) return;

    // Exclude breadcrumbs / navigation at runtime
    if (isInBreadcrumb(root)) return;
    if (isInNavigation(root)) return;
    if (isInCollectionSidebar(root)) return;
    if (isInHoverUI(root)) return;

    if (isInCollectionSidebar(root)) return;
    root.setAttribute(PROCESSED_ATTR, "1");

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node || !node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        if (isSkippableNode(node)) return NodeFilter.FILTER_REJECT;
        if (alreadyLinkedTextNode(node)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    const nodes = [];
    let n;
    while ((n = walker.nextNode())) nodes.push(n);

    let linksAdded = 0;

    for (const tn of nodes) {
      if (linksAdded >= MAX_LINKS_PER_ROOT) break;

      const original = tn.nodeValue;
      if (!original || !original.trim()) continue;

      for (const rx of regexChunks) {
        rx.lastIndex = 0;
        if (!rx.test(original)) continue;
        rx.lastIndex = 0;

        const frag = document.createDocumentFragment();
        let lastIndex = 0;
        let replacedAny = false;

        let m;
        while ((m = rx.exec(original)) !== null) {
          if (linksAdded >= MAX_LINKS_PER_ROOT) break;

          const match = m[0];
          const idx = m.index;
          const end = idx + match.length;

          const before = idx > 0 ? original.charAt(idx - 1) : "";
          const after  = end < original.length ? original.charAt(end) : "";
          if ((before && /[0-9A-Za-zÀ-ÖØ-öø-ÿ]/.test(before)) || (after && /[0-9A-Za-zÀ-ÖØ-öø-ÿ]/.test(after))) continue;

          const rule = map.get(normalizeKey(match));
          if (!rule) continue;

          // beschrijving heeft een eigen vendor-teller (max 2), los van de rest.
          // Per NODE bepaald (17-08): via het tab-open-event is de wortel soms de
          // tab-wrapper i.p.v. .tb_pr_desc zelf — root-detectie miste dan.
          const nodeInDesc = rootIsDesc || !!(tn.parentElement && tn.parentElement.closest &&
            tn.parentElement.closest(".tb_pr_desc, #ptabDesc, .product__description, .product-single__description"));
          const capKey = nodeInDesc && rule.isVendor ? rule.keyword + "@desc" : rule.keyword;
          const cap = rule.isVendor ? 2 : MAX_LINKS_PER_KEYWORD;
          const c = usedCounts.get(capKey) || 0;
          if (c >= cap) continue;

          if (isSameUrl(rule.url, window.location.href)) continue;

          if (idx > lastIndex) frag.appendChild(document.createTextNode(original.slice(lastIndex, idx)));

          const a = document.createElement("a");
          a.className = "nw-autolink";
          a.textContent = match;
          a.href = rule.url;
          frag.appendChild(a);

          lastIndex = end;
          replacedAny = true;
          linksAdded++;
          usedCounts.set(capKey, c + 1);
        }

        if (replacedAny) {
          if (lastIndex < original.length) frag.appendChild(document.createTextNode(original.slice(lastIndex)));
          tn.parentNode.replaceChild(frag, tn);
          break;
        }
      }
    }
  }

  // --- Runner + observers ---
  let compiled = null;

  function compileOnce() {
    const items = readMenuItems();
    if (!items) return null;

    const ctx = readContext();
    const category = chooseCategory(ctx);
    const pageNorm = buildPageNormFromAllText();

    const { map, keywords } = buildTargets(items, category, pageNorm, ctx);
    if (!keywords.length) return null;

    return {
      regexChunks: buildRegexChunks(keywords),
      map,
      usedCounts: new Map()
    };
  }

  function processVisible(container) {
    // Ensure collection filters are tagged as skip before scanning roots
    markCollectionFiltersAsSkip(container);

    if (!compiled) compiled = compileOnce();
    if (!compiled) return;

    const roots = collectCandidateRoots(container);
    if (!roots.length) return;

    // Beschrijving eerst (17-08): de vendor/keyword-caps zijn paginabreed en
    // werden opgesnoept door accordion/upsell-blokken voordat de zichtbare
    // hoofd-beschrijving aan de beurt was — die kreeg daardoor 0 links.
    const isDescriptionRoot = (el) =>
      !!(el.closest && el.closest(".product-description, .product-single__description, .rte"));
    roots.sort((a, b) => (isDescriptionRoot(b) ? 1 : 0) - (isDescriptionRoot(a) ? 1 : 0));

    for (const r of roots) {
      linkifyRoot(r, compiled.regexChunks, compiled.map, compiled.usedCounts);
    }
  }

  function scheduleProcess(container) {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => processVisible(container), { timeout: 900 });
    } else {
      setTimeout(() => processVisible(container), 0);
    }
  }

  let observerStarted = false;
  let debounceTimer = null;

  function startObserver() {
    if (observerStarted) return;
    observerStarted = true;

    // Observer only on main/body; nodes inside header/footer are ignored anyway by isInNavigation
    const target = document.querySelector("main") || document.body;
    if (!target) return;

    const observer = new MutationObserver((mutations) => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        for (const m of mutations) {
          if (m.type === "childList" && m.addedNodes && m.addedNodes.length) {
            m.addedNodes.forEach((node) => {
              if (!node || node.nodeType !== 1) return;
              scheduleProcess(node);
            });
          }
          if (m.type === "attributes" && m.target && m.target.nodeType === 1) {
            const el = m.target;
            if (isElementVisible(el)) scheduleProcess(el);
          }
        }
      }, RUN_DEBOUNCE_MS);
    });

    observer.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["open", "hidden", "style", "class", "aria-expanded"]
    });
  }

  function startEventHooks() {
    document.addEventListener("toggle", (e) => {
      const details = e.target;
      if (!details || details.tagName !== "DETAILS") return;
      scheduleProcess(details);
    }, true);

    document.addEventListener("click", (e) => {
      const t = e.target;
      if (!t) return;

      // Ignore clicks inside navigation entirely
      const el = (t.nodeType === 1) ? t : t.parentElement;
      if (el && isInNavigation(el)) return;

      if (el && isInCollectionSidebar(el)) return;
      if (el && isInHoverUI(el)) return;
      const summary = t.closest && t.closest("summary");
      if (summary) {
        const details = summary.closest("details");
        if (details) {
          setTimeout(() => scheduleProcess(details), 0);
          return;
        }
      }

      const btn = t.closest && t.closest("[aria-controls]");
      if (btn) {
        const id = btn.getAttribute("aria-controls");
        if (id) {
          const panel = document.getElementById(id);
          if (panel) {
            setTimeout(() => scheduleProcess(panel), 0);
          } else {
            const sec = btn.closest(".shopify-section") || btn.closest("section") || btn.closest("main");
            if (sec) setTimeout(() => scheduleProcess(sec), 0);
          }
        }
      }
    }, true);
  }

  function init() {
    // SEO: skip interlinking ONLY on brand top-collection pages
    // (e.g. /collections/arte-behang, /collections/cole-and-son-behang, /collections/glamora-behang).
    // Brand self-references on their own hub pages add no ranking value and risk anchor-text
    // cannibalization.
    //
    // Sub-collections (behangcollectie-*, murals-*, kleur-*, etc.) and all other templates
    // KEEP interlinking — long-tail → brand-hub links there do add real value.
    const _ctx = readContext();
    const _handle = (_ctx.collection_handle || "").toLowerCase();
    const isBrandTopPage = _ctx.brand_hub === true || (!!_handle && BRAND_PAGE_RE.test(_handle) && !SUB_COLLECTION_RE.test(_handle));
    if (isBrandTopPage) return;

    // Make sure collection filters (collection-sidebar.liquid / facets) are excluded
    markCollectionFiltersAsSkip(document);

    scheduleProcess(document);
    startObserver();
    startEventHooks();
  }

  function bootFetchItems() {
    // Gewicht-project 17-08: items-blob staat niet meer inline; async ophalen via de
    // Section Rendering API en onder hetzelfde id injecteren, zodat de rest van deze
    // module ongewijzigd werkt. Inline aanwezig (oude page-cache) = direct door.
    if (document.getElementById(DATA_ID)) { init(); return; }
    const preCtx = readContext();
    const preHandle = (preCtx.collection_handle || "").toLowerCase();
    if (preCtx.brand_hub === true || (preHandle && BRAND_PAGE_RE.test(preHandle) && !SUB_COLLECTION_RE.test(preHandle))) {
      return; // merk-hub: init() stopt daar toch — fetch besparen
    }
    const run = () => {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
      fetch(root + "?sections=sw-autolinks-data")
        .then((r) => (r.ok ? r.json() : null))
        .then((o) => {
          const html = o && o["sw-autolinks-data"];
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, "text/html");
          const el = doc.getElementById(DATA_ID);
          if (!el) return;
          const s = document.createElement("script");
          s.type = "application/json";
          s.id = DATA_ID;
          s.setAttribute("data-nw-autolinks", "");
          s.textContent = el.textContent;
          document.body.appendChild(s);
          init();
        })
        .catch(() => {});
    };
    if ("requestIdleCallback" in window) requestIdleCallback(run, { timeout: 4000 });
    else setTimeout(run, 1500);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootFetchItems);
  } else {
    bootFetchItems();
  }
})();
